import React, { useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useContent } from '../context/ContentContext';
import { Send, MapPin, Phone, Mail, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import { seoData, translations } from '../data/translations';
import { useLanguage } from '../context/LanguageContext';

// Golden Glow Line Component
const GoldenGlowLine = () => (
  <div className="relative h-[2px] bg-black overflow-hidden">
    <div 
      className="absolute top-0 h-full w-[300px]"
      style={{
        background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
        animation: 'glowLine 4s ease-in-out infinite',
        boxShadow: '0 0 20px #D4AF37, 0 0 40px #D4AF37',
      }}
    />
  </div>
);

// Animated Corner Frame Component (animation in index.css)
const AnimatedCornerFrame = ({ children, className = "" }) => (
  <div className={`group relative ${className}`}>
    {/* Top-left corner frame */}
    <div className="absolute top-0 left-0 w-1/2 h-1/2 overflow-hidden rounded-tl-3xl pointer-events-none">
      <div className="absolute top-0 left-0 w-[2px] h-full bg-black">
        <div 
          className="absolute left-0 w-full h-[60px]"
          style={{
            background: 'linear-gradient(180deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowVertical 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
      <div className="absolute top-0 left-0 h-[2px] w-full bg-black">
        <div 
          className="absolute top-0 h-full w-[60px]"
          style={{
            background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowHorizontal 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
    </div>
    
    {/* Bottom-right corner frame */}
    <div className="absolute bottom-0 right-0 w-1/2 h-1/2 overflow-hidden rounded-br-3xl pointer-events-none">
      <div className="absolute bottom-0 right-0 w-[2px] h-full bg-black">
        <div 
          className="absolute right-0 w-full h-[60px]"
          style={{
            background: 'linear-gradient(0deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowVerticalReverse 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
      <div className="absolute bottom-0 right-0 h-[2px] w-full bg-black">
        <div 
          className="absolute bottom-0 h-full w-[60px]"
          style={{
            background: 'linear-gradient(270deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowHorizontalReverse 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
    </div>
    {children}
  </div>
);

const ContactPage = () => {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', subject: '', message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useLanguage();
  const { contactInfo } = useContent();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      setIsSubmitted(true);
      setTimeout(() => { 
        setIsSubmitted(false); 
        setFormData({ firstName: '', lastName: '', email: '', subject: '', message: '' }); 
      }, 3000);
    } catch (err) {
      setError(t('error', translations.contact));
      if (process.env.NODE_ENV === 'development') {
        console.error('Contact form error:', err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Use CMS contact info if available
  const contactDetails = [
    { icon: MapPin, title: t('address', { de: 'Adresse', en: 'Address' }), content: contactInfo?.address || 'Berlin, Deutschland', subtext: t('visit', { de: 'Besuchen Sie uns', en: 'Visit us' }) },
    { icon: Phone, title: t('phone', { de: 'Telefon', en: 'Phone' }), content: contactInfo?.phone || '+49 30 123 456 78', subtext: contactInfo?.hours || t('hours', { de: 'Mo-Fr 8:00-18:00', en: 'Mon-Fri 8:00-18:00' }) },
    { icon: Mail, title: t('emailTitle', { de: 'E-Mail', en: 'Email' }), content: contactInfo?.email || 'info@yetkin-dienstleistungen.de', subtext: t('response', { de: 'Antwort in 24h', en: 'Response in 24h' }) },
    { icon: Clock, title: t('emergency', { de: 'Notfall', en: 'Emergency' }), content: t('allDay', { de: 'Rund um die Uhr', en: 'Around the clock' }), subtext: t('emergencyAvail', { de: 'Im Notfall erreichbar', en: 'Available in emergencies' }) }
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <SEO 
        title={seoData.contact.title}
        description={seoData.contact.description}
      />
      <Navbar />
      <main>
        <section className="relative pt-40 pb-20">
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-20">
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="h-px w-12 bg-[#D4AF37]" />
                <span className="text-black/50 text-sm font-medium uppercase tracking-[0.3em]">{t('contact', translations.nav)}</span>
                <div className="h-px w-12 bg-[#D4AF37]" />
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-6">{t('title', translations.contact)}</h1>
              <p className="text-xl text-black/60 max-w-2xl mx-auto">{t('subtitle', translations.contact)}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {contactDetails.map((d) => (
                <div key={d.title} className="group p-6 rounded-2xl bg-white border border-black/5 hover:border-[#D4AF37]/30 transition-all duration-500 hover:shadow-xl text-center">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/20 transition-colors mx-auto">
                    <d.icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-black font-semibold mb-1">{d.title}</h3>
                  <p className="text-black font-medium mb-1 text-xs sm:text-sm">{d.content}</p>
                  <p className="text-black/50 text-xs">{d.subtext}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <GoldenGlowLine />

        <section className="py-20 bg-[#F9F9F9]">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <AnimatedCornerFrame className="p-10 md:p-14 rounded-3xl bg-white border border-black/5 shadow-xl">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-10 h-10 text-green-500" /></div>
                  <h3 className="text-2xl font-bold text-black mb-3">{t('successTitle', { de: 'Nachricht gesendet!', en: 'Message sent!' })}</h3>
                  <p className="text-black/60">{t('success', translations.contact)}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
                      {error}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-black">{t('firstName', translations.contact)}</Label>
                      <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} required className="bg-[#F9F9F9] border-black/10 text-black placeholder:text-black/40 focus:border-[#D4AF37] h-12" placeholder="Max" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-black">{t('lastName', translations.contact)}</Label>
                      <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} required className="bg-[#F9F9F9] border-black/10 text-black placeholder:text-black/40 focus:border-[#D4AF37] h-12" placeholder="Mustermann" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-black">{t('email', translations.contact)}</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="bg-[#F9F9F9] border-black/10 text-black placeholder:text-black/40 focus:border-[#D4AF37] h-12" placeholder="max@beispiel.de" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-black">{t('subject', translations.contact)}</Label>
                    <Input id="subject" name="subject" value={formData.subject} onChange={handleChange} required className="bg-[#F9F9F9] border-black/10 text-black placeholder:text-black/40 focus:border-[#D4AF37] h-12" placeholder={t('subjectPlaceholder', { de: 'Wie können wir helfen?', en: 'How can we help?' })} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-black">{t('message', translations.contact)}</Label>
                    <Textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="bg-[#F9F9F9] border-black/10 text-black placeholder:text-black/40 focus:border-[#D4AF37] resize-none" placeholder={t('messagePlaceholder', { de: 'Ihre Nachricht...', en: 'Your message...' })} />
                  </div>
                  <Button type="submit" disabled={isLoading} className="w-full h-14 bg-[#D4AF37] hover:bg-[#C9A227] text-[#003366] font-semibold text-lg rounded-xl">
                    {isLoading ? <span className="flex items-center gap-2"><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />{t('sending', translations.contact)}</span> : <span className="flex items-center gap-2">{t('send', translations.contact)}<Send size={20} /></span>}
                  </Button>
                </form>
              )}
            </AnimatedCornerFrame>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
